import { BookOpen, ExternalLink, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next"; // Add this import

const StatBox = ({ value, description, url }) => {
  // Check if URL is internal (starts with /)
  const isInternalLink = url && url.startsWith("/");

  // For internal links, use a regular anchor without target="_blank"
  if (isInternalLink) {
    return (
      <a
        href={url}
        className={`rounded-lg bg-white border border-[#B82A1E] hover:bg-[#B82A1E] p-4 group transition-colors duration-300 cursor-pointer block relative`}
      >
        <div className="text-3xl font-bold text-[#B82A1E] group-hover:text-white transition-colors duration-300">
          {value}
        </div>
        <div className="text-sm text-[#B82A1E] group-hover:text-white transition-colors duration-300 flex items-center gap-1">
          {description}
        </div>
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <ExternalLink size={16} className="text-white" />
        </div>
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`rounded-lg bg-white border border-[#B82A1E] hover:bg-[#B82A1E] p-4 group transition-colors duration-300 ${
        url ? "cursor-pointer" : ""
      } block relative`}
    >
      <div className="text-3xl font-bold text-[#B82A1E] group-hover:text-white transition-colors duration-300">
        {value}
      </div>
      <div className="text-sm text-[#B82A1E] group-hover:text-white transition-colors duration-300 flex items-center gap-1">
        {description}
        <ExternalLink size={12} className="ml-1 opacity-70" />
      </div>
      <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <ExternalLink size={16} className="text-white" />
      </div>
    </a>
  );
};

const SocialMediaCard = ({ icon, value, label, url }) => {
  return (
    <div
      className="relative rounded-xl bg-white p-7 overflow-hidden border border-[#B82A1E]/20 shadow-md hover:shadow-lg transition-shadow h-full"
      style={{
        background: `linear-gradient(135deg, #FFF8F8 0%, #FFF 100%)`,
      }}
    >
      <div className="flex flex-row h-full">
        <div className="w-2/3">
          <div className="mb-1">
            <span className="text-5xl font-bold text-[#B82A1E]">{value}</span>
          </div>
          <div className="text-gray-600 text-md">{label}</div>
        </div>
        <div className="w-1/3 flex flex-col justify-between">
          {/* Top right arrow link */}
          <div className="flex justify-end">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#F9D3CF] p-2 rounded-full hover:bg-[#B82A1E] hover:text-white transition-colors"
            >
              <ArrowUpRight className="w-5 h-5" />
            </a>
          </div>

          {/* Bottom right icon */}
          <div className="flex justify-end items-end mt-auto">
            <div className="text-[#B82A1E] h-12 w-12 flex items-center justify-center text-4xl">
              {icon}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const JourneySection = () => {
  const { t } = useTranslation(); // Add translation hook
  
  return (
    <section className=" bg-white">
      <div className="container mx-auto px-5">
        {/* Header */}
        <div className="mb-12 text-center">
          <h2 className="text-4xl mb-4 pt-16 relative inline-block after:block after:w-20 after:h-1 after:bg-[#8a1f16] after:mx-auto after:mt-2">
            {t("journey.sectionTitle")}
          </h2>
          <p className="mx-auto max-w-3xl text-lg text-gray-600">
            {t("journey.sectionDescription")}
          </p>
        </div>

        {/* Social Media Dashboard */}
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* First Column */}
            <div className="grid grid-cols-1 grid-rows-2 gap-6 h-full">
              {/* YouTube Card */}
              <SocialMediaCard
                icon={
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 48.00 48.00"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    fill="currentColor"
                    className="text-red-600"
                  >
                    <g id="SVGRepo_iconCarrier">
                      <title>Youtube</title>
                      <desc>YouTube Icon</desc>
                      <g
                        id="Icons"
                        strokeWidth="0.00048000000000000007"
                        fill="none"
                        fillRule="evenodd"
                      >
                        <g
                          id="Color-"
                          transform="translate(-200.000000, -368.000000)"
                          fill="currentColor"
                        >
                          <path
                            d="M219.044,391.269916 L219.0425,377.687742 L232.0115,384.502244 L219.044,391.269916 Z M247.52,375.334163 C247.52,375.334163 247.0505,372.003199 245.612,370.536366 C243.7865,368.610299 241.7405,368.601235 240.803,368.489448 C234.086,368 224.0105,368 224.0105,368 L223.9895,368 C223.9895,368 213.914,368 207.197,368.489448 C206.258,368.601235 204.2135,368.610299 202.3865,370.536366 C200.948,372.003199 200.48,375.334163 200.48,375.334163 C200.48,375.334163 200,379.246723 200,383.157773 L200,386.82561 C200,390.73817 200.48,394.64922 200.48,394.64922 C200.48,394.64922 200.948,397.980184 202.3865,399.447016 C204.2135,401.373084 206.612,401.312658 207.68,401.513574 C211.52,401.885191 224,402 224,402 C224,402 234.086,401.984894 240.803,401.495446 C241.7405,401.382148 243.7865,401.373084 245.612,399.447016 C247.0505,397.980184 247.52,394.64922 247.52,394.64922 C247.52,394.64922 248,390.73817 248,386.82561 L248,383.157773 C248,379.246723 247.52,375.334163 247.52,375.334163 L247.52,375.334163 Z"
                            id="Youtube"
                          ></path>
                        </g>
                      </g>
                    </g>
                  </svg>
                }
                value="163K"
                label={t("journey.youtubeSubscribers")}
                url="https://www.youtube.com/@avdheshanandg"
              />

              {/* Instagram Card */}
              <SocialMediaCard
                icon={
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="2"
                      y="2"
                      width="28"
                      height="28"
                      rx="6"
                      fill="url(#paint0_radial_87_7153)"
                    ></rect>
                    <rect
                      x="2"
                      y="2"
                      width="28"
                      height="28"
                      rx="6"
                      fill="url(#paint1_radial_87_7153)"
                    ></rect>
                    <rect
                      x="2"
                      y="2"
                      width="28"
                      height="28"
                      rx="6"
                      fill="url(#paint2_radial_87_7153)"
                    ></rect>
                    <path
                      d="M23 10.5C23 11.3284 22.3284 12 21.5 12C20.6716 12 20 11.3284 20 10.5C20 9.67157 20.6716 9 21.5 9C22.3284 9 23 9.67157 23 10.5Z"
                      fill="white"
                    ></path>
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M16 21C18.7614 21 21 18.7614 21 16C21 13.2386 18.7614 11 16 11C13.2386 11 11 13.2386 11 16C11 18.7614 13.2386 21 16 21ZM16 19C17.6569 19 19 17.6569 19 16C19 14.3431 17.6569 13 16 13C14.3431 13 13 14.3431 13 16C13 17.6569 14.3431 19 16 19Z"
                      fill="white"
                    ></path>
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6 15.6C6 12.2397 6 10.5595 6.65396 9.27606C7.2292 8.14708 8.14708 7.2292 9.27606 6.65396C10.5595 6 12.2397 6 15.6 6H16.4C19.7603 6 21.4405 6 22.7239 6.65396C23.8529 7.2292 24.7708 8.14708 25.346 9.27606C26 10.5595 26 12.2397 26 15.6V16.4C26 19.7603 26 21.4405 25.346 22.7239C24.7708 23.8529 23.8529 24.7708 22.7239 25.346C21.4405 26 19.7603 26 16.4 26H15.6C12.2397 26 10.5595 26 9.27606 25.346C8.14708 24.7708 7.2292 23.8529 6.65396 22.7239C6 21.4405 6 19.7603 6 16.4V15.6ZM15.6 8H16.4C18.1132 8 19.2777 8.00156 20.1779 8.0751C21.0548 8.14674 21.5032 8.27659 21.816 8.43597C22.5686 8.81947 23.1805 9.43139 23.564 10.184C23.7234 10.4968 23.8533 10.9452 23.9249 11.8221C23.9984 12.7223 24 13.8868 24 15.6V16.4C24 18.1132 23.9984 19.2777 23.9249 20.1779C23.8533 21.0548 23.7234 21.5032 23.564 21.816C23.1805 22.5686 22.5686 23.1805 21.816 23.564C21.5032 23.7234 21.0548 23.8533 20.1779 23.9249C19.2777 23.9984 18.1132 24 16.4 24H15.6C13.8868 24 12.7223 23.9984 11.8221 23.9249C10.9452 23.8533 10.4968 23.7234 10.184 23.564C9.43139 23.1805 8.81947 22.5686 8.43597 21.816C8.27659 21.5032 8.14674 21.0548 8.0751 20.1779C8.00156 19.2777 8 18.1132 8 16.4V15.6C8 13.8868 8.00156 12.7223 8.0751 11.8221C8.14674 10.9452 8.27659 10.4968 8.43597 10.184C8.81947 9.43139 9.43139 8.81947 10.184 8.43597C10.4968 8.27659 10.9452 8.14674 11.8221 8.0751C12.7223 8.00156 13.8868 8 15.6 8Z"
                      fill="white"
                    ></path>
                    <defs>
                      <radialGradient
                        id="paint0_radial_87_7153"
                        cx="0"
                        cy="0"
                        r="1"
                        gradientUnits="userSpaceOnUse"
                        gradientTransform="translate(12 23) rotate(-55.3758) scale(25.5196)"
                      >
                        <stop stopColor="#B13589"></stop>
                        <stop offset="0.79309" stopColor="#C62F94"></stop>
                        <stop offset="1" stopColor="#8A3AC8"></stop>
                      </radialGradient>
                      <radialGradient
                        id="paint1_radial_87_7153"
                        cx="0"
                        cy="0"
                        r="1"
                        gradientUnits="userSpaceOnUse"
                        gradientTransform="translate(11 31) rotate(-65.1363) scale(22.5942)"
                      >
                        <stop stopColor="#E0E8B7"></stop>
                        <stop offset="0.444662" stopColor="#FB8A2E"></stop>
                        <stop offset="0.71474" stopColor="#E2425C"></stop>
                        <stop offset="1" stopColor="#E2425C" stopOpacity="0"></stop>
                      </radialGradient>
                      <radialGradient
                        id="paint2_radial_87_7153"
                        cx="0"
                        cy="0"
                        r="1"
                        gradientUnits="userSpaceOnUse"
                        gradientTransform="translate(0.500002 3) rotate(-8.1301) scale(38.8909 8.31836)"
                      >
                        <stop offset="0.156701" stopColor="#406ADC"></stop>
                        <stop offset="0.467799" stopColor="#6A45BE"></stop>
                        <stop offset="1" stopColor="#6A45BE" stopOpacity="0"></stop>
                      </radialGradient>
                    </defs>
                  </svg>
                }
                value="3.3M"
                label={t("journey.instagramFollowers")}
                url="https://www.instagram.com/avdheshanandg_official/"
              />
            </div>

            {/* Website Views Card - MIDDLE COLUMN */}
            <div
              className="relative rounded-xl bg-white p-6 overflow-hidden border border-[#B82A1E]/20 shadow-md hover:shadow-lg transition-shadow h-full flex flex-col"
              style={{
                background: `linear-gradient(135deg, #FFF8F8 0%, #FFF 100%)`,
              }}
            >
              <div className="flex items-center mb-4">
                <BookOpen className="w-8 h-8 text-[#8a1f16]" />
                <span className="ml-2 text-xl font-semibold text-[#8a1f16]">
                  {t("journey.repository.title")}
                </span>
              </div>

              <div className="flex flex-col space-y-4">
                <StatBox
                  value={t("journey.repository.books.count")}
                  description={t("journey.repository.books.description")}
                  url="/books"
                />
                <StatBox
                  value={t("journey.repository.articles.count")}
                  description={t("journey.repository.articles.description")}
                  url="/articles"
                />
                <StatBox
                  value={t("journey.repository.videos.count")}
                  description={t("journey.repository.videos.description")}
                  url="/video-series"
                />
                <StatBox 
                  value={t("journey.repository.commentaries.count")} 
                  description={t("journey.repository.commentaries.description")} 
                  url="/articles" 
                />
              </div>
            </div>

            {/* Third Column */}
            <div className="grid grid-cols-1 grid-rows-2 gap-6 h-full">
              {/* Facebook Card */}
              <SocialMediaCard
                icon={
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 48 48"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    fill="none"
                  >
                    <g id="Icons" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                      <g id="Color-" transform="translate(-200.000000, -160.000000)" fill="#4460A0">
                        <path
                          d="M225.638355,208 L202.649232,208 C201.185673,208 200,206.813592 200,205.350603 L200,162.649211 C200,161.18585 201.185859,160 202.649232,160 L245.350955,160 C246.813955,160 248,161.18585 248,162.649211 L248,205.350603 C248,206.813778 246.813769,208 245.350955,208 L233.119305,208 L233.119305,189.411755 L239.358521,189.411755 L240.292755,182.167586 L233.119305,182.167586 L233.119305,177.542641 C233.119305,175.445287 233.701712,174.01601 236.70929,174.01601 L240.545311,174.014333 L240.545311,167.535091 C239.881886,167.446808 237.604784,167.24957 234.955552,167.24957 C229.424834,167.24957 225.638355,170.625526 225.638355,176.825209 L225.638355,182.167586 L219.383122,182.167586 L219.383122,189.411755 L225.638355,189.411755 L225.638355,208 L225.638355,208 Z"
                          id="Facebook"
                        ></path>
                      </g>
                    </g>
                  </svg>
                }
                value="1.4M"
                label={t("journey.facebookFollowers")}
                url="https://www.facebook.com/AvdheshanandG/"
              />

              {/* Twitter Card */}
              <SocialMediaCard
                icon={
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 -4 48 48"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    fill="none"
                  >
                    <g id="Icons" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                      <g id="Color-" transform="translate(-300.000000, -164.000000)" fill="#00AAEC">
                        <path
                          d="M348,168.735283 C346.236309,169.538462 344.337383,170.081618 342.345483,170.324305 C344.379644,169.076201 345.940482,167.097147 346.675823,164.739617 C344.771263,165.895269 342.666667,166.736006 340.418384,167.18671 C338.626519,165.224991 336.065504,164 333.231203,164 C327.796443,164 323.387216,168.521488 323.387216,174.097508 C323.387216,174.88913 323.471738,175.657638 323.640782,176.397255 C315.456242,175.975442 308.201444,171.959552 303.341433,165.843265 C302.493397,167.339834 302.008804,169.076201 302.008804,170.925244 C302.008804,174.426869 303.747139,177.518238 306.389857,179.329722 C304.778306,179.280607 303.256911,178.821235 301.9271,178.070061 L301.9271,178.194294 C301.9271,183.08848 305.322064,187.17082 309.8299,188.095341 C309.004402,188.33225 308.133826,188.450704 307.235077,188.450704 C306.601162,188.450704 305.981335,188.390033 305.381229,188.271578 C306.634971,192.28169 310.269414,195.2026 314.580032,195.280607 C311.210424,197.99061 306.961789,199.605634 302.349709,199.605634 C301.555203,199.605634 300.769149,199.559408 300,199.466956 C304.358514,202.327194 309.53689,204 315.095615,204 C333.211481,204 343.114633,188.615385 343.114633,175.270495 C343.114633,174.831347 343.106181,174.392199 343.089276,173.961719 C345.013559,172.537378 346.684275,170.760563 348,168.735283"
                          id="Twitter"
                        ></path>
                      </g>
                    </g>
                  </svg>
                }
                value="342.1K"
                label={t("journey.twitterFollowers")}
                url="https://twitter.com/AvdheshanandG"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JourneySection;

// <div className="flex items-center mb-4">{icon}</div>
// <div className="mb-1">
//   <span className="text-5xl font-bold text-[#B82A1E]">{value}</span>
// </div>
// <div className="text-gray-600 text-sm">{label}</div>
// <a
//   href={url}
//   target="_blank"
//   rel="noopener noreferrer"
//   className="absolute top-4 right-4 bg-[#F9D3CF] p-2 rounded-full hover:bg-[#B82A1E] hover:text-white transition-colors"
// >
//   <ArrowUpRight className="w-5 h-5" />
// </a>